import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import FlightAmendmentModal from "./FlightAmendmentModal";
import DefaultData from "../../CompanyData/DefaultData";
import moment from "moment";
import FlightTabs from "../Flight/FlightTabs";
import { toast } from "react-toastify";

const FlightBookings = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localDate = new Date(today.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [rawResponse, setRawResponse] = useState({});

  // Pagination States
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // ✅ Track whether user has performed a search yet
  const [hasSearched, setHasSearched] = useState(false);

  // Filter States
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [customerName, setCustomerName] = useState("");
  const [bookingNo, setBookingNo] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [source, setSource] = useState("CLIENT");

  // Stats State
  const [stats, setStats] = useState({
    totalBookings: 0,
    confirmedCount: 0,
    pendingCount: 0,
    cancelledCount: 0,
    holdCount: 0,
    cancelRequestedCount: 0,
    paymentSuccessCount: 0,
    paymentPendingCount: 0,
    paymentFailedCount: 0,
    b2CCount: 0,
    b2BCount: 0,
    b2ECount: 0,
    portalCount: 0,
    todayBookings: 0
  });

  // Amendment Modal States
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Fetch Stats - Similar to GetByParentCompany
  const fetchStats = async () => {
    if (!user?.cid) return;
    try {
      const queryParams = new URLSearchParams({
        source: source || "",
        customerName: customerName || "",
        bookingNo: bookingNo || "",
        bookingStatus: bookingStatus || "",
        paymentStatus: paymentStatus || "",
        fromDate: fromDate || "",
        toDate: toDate || ""
      }).toString();

      const url = `${config.GETFLIGHTBOOKINGSTATS}/${user?.cid}?${queryParams}`;
      const response = await apiService.getSingle(url);
      const data = response?.data || response;
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch flight booking stats:", error);
    }
  };

  // Get Data - Similar to GetByParentCompany
  const GetData = async (pNum = 1) => {
    if (!user?.cid) return;
    setPageLoading(true);

    const queryParams = new URLSearchParams({
      customerName: customerName || "",
      bookingNo: bookingNo || "",
      bookingStatus: bookingStatus || "",
      paymentStatus: paymentStatus || "",
      fromDate: fromDate || "",
      toDate: toDate || "",
      source: source || "",
      pageNumber: pNum,
      pageSize: pageSize
    }).toString();

    try {
      const url = `${config.GETFLIGHTBOOKINGSBYPARENTCOMPANYID}?ParentCompanyId=${user?.cid}&${queryParams}`;
      const res = await apiService.post(url);

      if (res) {
        setLists(res.data || []);
        setPageNumber(res.pageNumber || 1);
        setPageSize(res.pageSize || 20);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setRawResponse(res);
      }
    } catch (error) {
      console.error("Failed to fetch flight bookings:", error);
      setLists([]);
    } finally {
      setPageLoading(false);
    }
  };

  const handleConfirm = async (e, FlightBookingId) => {
    setPageLoading(true);
    await apiService.post(`${config.CONFIRMHOLDFLIGHTBOOKING}/${FlightBookingId}`);
    navigate("/flight/booking/" + FlightBookingId);
  };

  // Delete handler
  const handleDelete = async (flightBookingId, bookingNo) => {
    if (!window.confirm(`Are you sure you want to delete booking #${bookingNo}?`)) {
      return;
    }

    setPageLoading(true);
    try {
      const url = `${config.DELETEFLIGHTBOOKING}/${flightBookingId}`;
      const result = await apiService.put(url);

      if (result) {
        toast.success(`Booking deleted successfully`);
        GetData(pageNumber);
        fetchStats();
      } else {
        toast.error("Failed to delete booking");
      }
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("An error occurred while deleting the booking");
    } finally {
      setPageLoading(false);
    }
  };

  // Open Amendment Modal
  const openAmendmentModal = (booking) => {
    setSelectedBooking(booking);
    setShowAmendmentModal(true);
  };

  // Close Amendment Modal
  const closeAmendmentModal = () => {
    setShowAmendmentModal(false);
    setSelectedBooking(null);
  };

  // Handle successful amendment submission
  const handleAmendmentSuccess = () => {
    GetData(pageNumber);
    fetchStats();
  };

  // Pagination Logic
  const GetPageData = (number) => {
    setPageNumber(number);
    GetData(number);
  };

  const GetFirstPageData = () => {
    if (rawResponse.previousPage !== null) GetData(1);
  };

  const GetLastPageData = () => {
    if (rawResponse.nextPage !== null) GetData(totalPages);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const handleSearch = () => {
    setHasSearched(true);
    setPageNumber(1);
    GetData(1);
    fetchStats();
  };

  const handleReset = () => {
    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    setCustomerName("");
    setBookingNo("");
    setBookingStatus("");
    setPaymentStatus("");
    setSource("CLIENT");
    setPageNumber(1);
    setHasSearched(false);
    setLists([]);
    setTotalRecords(0);
    setTotalPages(0);
    setRawResponse({});
    setStats({
      totalBookings: 0,
      confirmedCount: 0,
      pendingCount: 0,
      cancelledCount: 0,
      holdCount: 0,
      cancelRequestedCount: 0,
      paymentSuccessCount: 0,
      paymentPendingCount: 0,
      paymentFailedCount: 0,
      b2CCount: 0,
      b2BCount: 0,
      b2ECount: 0,
      portalCount: 0,
      todayBookings: 0
    });
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "bg-success";
      case "PENDING":
        return "bg-warning text-dark";
      case "CANCELLED":
        return "bg-danger";
      case "HOLD":
        return "bg-info";
      case "CANCELREQUESTED":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const getPaymentBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
        return "bg-success";
      case "PENDING":
        return "bg-warning text-dark";
      case "FAILED":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  if (loading) return <p>Loading...</p>;
  if (!user) { navigate("/"); return null; }

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="flights" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <FlightTabs pageName="FLIGHTBOOKINGS" />
                </div>
                <div className="card-body">

                  {/* Compact Stats Cards - Single Row */}
                  {/* Compact Stats Cards - Single Row */}
                  <div className="row g-1 mb-3">
                    <div className="col-12">
                      <div className="d-flex flex-wrap gap-1">
                        {/* Booking Status Cards */}
                        <div className="card bg-primary text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Total Bookings</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.totalBookings}</h6>
                          </div>
                        </div>
                        <div className="card bg-success text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Confirmed Bookings</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.confirmedCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-warning text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Pending Bookings</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.pendingCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-danger text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Cancelled Bookings</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.cancelledCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-info text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Hold Bookings</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.holdCount}</h6>
                          </div>
                        </div>

                        {/* Payment Status Cards */}
                        <div className="card bg-success text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Success Payments</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.paymentSuccessCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-warning text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Pending Payments</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.paymentPendingCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-danger text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Failed Payments</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.paymentFailedCount}</h6>
                          </div>
                        </div>

                        {/* Source Cards */}
                        <div className="card bg-primary text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>Portal</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.portalCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-info text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>B2C</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.b2CCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-secondary text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>B2B</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.b2BCount}</h6>
                          </div>
                        </div>
                        <div className="card bg-dark text-white" style={{ flex: '1 0 auto', minWidth: '60px', maxWidth: '100px' }}>
                          <div className="card-body py-1 px-2 text-center">
                            <small className="text-dark-50 d-block" style={{ fontSize: '8px' }}>B2E</small>
                            <h6 className="text-white mb-0" style={{ fontSize: '14px' }}>{stats.b2ECount}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className="row mb-4 g-3 bg-light p-3 rounded">
                    <div className="col-md-2">
                      <label className="form-label">From Date</label>
                      <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">To Date</label>
                      <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Source</label>
                      <select className="form-select" value={source} onChange={(e) => setSource(e.target.value)}>
                        <option value="CLIENT">PORTAL</option>
                        <option value="CUSTOMER">CUSTOMER</option>
                        <option value="CORPORATE">CORPORATE</option>
                        <option value="AGENT">AGENT</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Customer Name</label>
                      <input
                        type="text" className="form-control" placeholder="Search Name..."
                        value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking No</label>
                      <input
                        type="text" className="form-control" placeholder="Ref No..."
                        value={bookingNo} onChange={(e) => setBookingNo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking Status</label>
                      <select className="form-select" value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="CONFIRMED">CONFIRMED</option>
                        <option value="PENDING">PENDING</option>
                        <option value="HOLD">HOLD</option>
                        <option value="CANCELLED">CANCELLED</option>
                        <option value="CANCELREQUESTED">CANCEL REQUESTED</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Payment Status</label>
                      <select className="form-select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                        <option value="">All Payment</option>
                        <option value="SUCCESS">SUCCESS</option>
                        <option value="PENDING">PENDING</option>
                        <option value="FAILED">FAILED</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">&nbsp;</label>
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                          <i className="ri-search-line me-1"></i> Search
                        </button>
                        <button className="btn btn-danger flex-grow-1" onClick={handleReset}>
                          <i className="ri-refresh-line me-1"></i> Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Table Section */}
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle" id="customerTable">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th>Id</th>
                          <th>Company Details</th>
                          <th>Supplier</th>
                          <th>Booking No</th>
                          <th>Booking Date</th>
                          <th>Journey</th>
                          <th>Departure</th>
                          <th>Traveller</th>
                          <th>Branch</th>
                          <th>Booking Status</th>
                          <th>Payment Status</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {pageLoading ? (
                          <tr>
                            <td colSpan={13} className="text-center">
                              <img src="/assets/images/loader.webp" width="64px" height="64px" alt="loader" loading="lazy" />
                              <p>Loading, Please wait...</p>
                            </td>
                          </tr>
                        ) : lists.length > 0 ? (
                          lists.map((v, index) => (
                            <tr key={v.flightBookingId}>
                              <td>{(pageNumber - 1) * pageSize + (index + 1)}</td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="fw-bold text-primary">
                                    {v.companyName || "-"}
                                  </span>
                                  <small className="text-muted">
                                    {v.branchName || "-"}
                                  </small>
                                </div>
                              </td>
                              <td>{v.supplierName}<br /></td>
                              <td>
                                {v.bookingNo}<br />
                                <small className="text-muted">PNR: {v.pnr}</small>
                                <br />
                                <small className="text-muted">SupplierBookingId: {v.supplierBookingId}</small>
                              </td>
                              <td>{v.bookingDate ? moment(v.bookingDate).format("DD MMM YY HH:mm") : "—"}</td>
                              <td>{v.flightSearchRequest?.journeyType}</td>
                              <td>
                                {v.flightSearchRequest?.flightTravelerPoints?.[0]?.from} - {v.flightSearchRequest?.flightTravelerPoints?.[0]?.to}
                                <br />
                                <small>{moment(v.departureDate).format("DD MMM YY")}</small>
                              </td>
                              <td>{v.travellerName}</td>
                              <td>{v.branchName}</td>
                              <td>
                                <span className={`badge ${getStatusBadge(v.bookingStatus)}`}>
                                  {v.bookingStatus}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getPaymentBadge(v.paymentStatus)}`}>
                                  {v.paymentStatus}
                                </span>
                              </td>
                              <td>{Math.round((parseFloat(v.totalPrice) || 0))} INR</td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">

                                  {v.bookingStatus === "HOLD" && DefaultData.TENANTID !== "zotrai" && (
                                    <li className="list-inline-item">
                                      <button onClick={(e) => handleConfirm(e, v.flightBookingId)} className="btn btn-sm btn-secondary">
                                        CONFIRM
                                      </button>
                                    </li>
                                  )}

                                  <li className="list-inline-item">
                                    <Link to={"/flight/booking/" + v.flightBookingId} className="btn btn-sm btn-primary">
                                      <i className="ri-eye-line me-1"></i>
                                    </Link>
                                  </li>
                                  {/* <li className="list-inline-item">
                                        <Link 
                                          to={`/flight/booking-accounting/${v.flightBookingId}`} 
                                              className="btn btn-sm btn-soft-info"
                                               title="View Invoices & Ledgers"
                                        >
                                          <i className="ri-file-list-3-line"></i> INVOICES
                                        </Link>
                                      </li> */}


                                  {/* Delete Button */}
                                  {v.bookingStatus !== "CONFIRMED" && v.paymentStatus !== "SUCCESS" && (
                                    <li className="list-inline-item">
                                      <button
                                        onClick={() => handleDelete(v.flightBookingId, v.bookingNo)}
                                        className="btn btn-sm btn-danger"
                                        title="Delete Booking"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </li>
                                  )}

                                  {/* Only show AMEND button if booking is CONFIRMED, payment SUCCESS, and departure date is today or future */}
                                  {v.bookingStatus === "CONFIRMED" && v.paymentStatus === "SUCCESS" &&
                                    moment(v.departureDate).isAfter(moment(), 'day') && (
                                      <li className="list-inline-item">
                                        <button
                                          onClick={() => openAmendmentModal(v)}
                                          className="btn btn-sm btn-warning"
                                          title="Raise Amendment Request"
                                        >
                                          <i className="ri-edit-line me-1"></i> AMEND
                                        </button>
                                      </li>
                                    )}
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={13} className="text-center text-muted py-4">
                              <i className="ri-search-line fs-3 d-block mb-2"></i>
                              {hasSearched
                                ? "No flight bookings found for the selected filters."
                                : <>Click <strong>Search</strong> to view flight bookings.</>}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer */}
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing <span className="fw-semibold">{lists.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span> Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className={`page-item ${rawResponse.previousPage === null ? "disabled" : ""}`} onClick={GetFirstPageData}>
                        <Link className="page-link">First</Link>
                      </li>
                      {pageNumbers.map((number) => (
                        <li key={number} className={`page-item ${pageNumber === number ? "active" : ""}`} onClick={() => GetPageData(number)}>
                          <Link className="page-link">{number}</Link>
                        </li>
                      ))}
                      <li className={`page-item ${rawResponse.nextPage === null ? "disabled" : ""}`} onClick={GetLastPageData}>
                        <Link className="page-link">Last</Link>
                      </li>
                    </ul>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Amendment Modal - Separate Component */}
      <FlightAmendmentModal
        show={showAmendmentModal}
        booking={selectedBooking}
        onClose={closeAmendmentModal}
        onSuccess={handleAmendmentSuccess}
        user={user}
      />
    </div>
  );
};

export default FlightBookings;